// src/domain/pro/resolveProStatus.ts
/**
 * Pro Status Resolution - Single Source of Truth
 * ===============================================
 * 
 * This is the ONLY place where Pro status is computed.
 * All UI components must consume ProStatusResolution, not read Firestore fields directly.
 * 
 * Design Principles:
 * 1. Pure function - no side effects, no Firestore reads
 * 2. Deterministic - same inputs always produce same output
 * 3. Single responsibility - status resolution only
 * 4. Exhaustive - handles all possible state combinations
 * 5. UI-agnostic - returns data, components decide how to render
 */

import type { User } from 'firebase/auth';
import type { Professional } from '@/types/professional';
import { validateLicenses, type LicenseDocument } from '@/lib/licenseValidation';

// ============================================================================
// STATUS ENUM - Lifecycle States
// ============================================================================

/**
 * ProStatus represents the lifecycle state of a professional account
 * States are mutually exclusive and prioritized by severity
 */
export type ProStatus =
  // Critical blocking states (prevent all marketplace activity)
  | 'SUSPENDED_LICENSE'        // No active license (verified but license expired/missing)
  | 'SUSPENDED_ADMIN'          // Account paused/banned by admin
  | 'REJECTED'                 // Verification application rejected
  
  // Onboarding states (progressing toward verified)
  | 'ONBOARDING'               // New pro, never submitted verification
  | 'PENDING_VERIFICATION'     // Verification submitted, awaiting admin review
  | 'REVERIFICATION_REQUIRED'  // Admin flagged for re-verification
  
  // Active states (can use platform)
  | 'VERIFIED_ACTIVE'          // Verified + active license + subscribed
  | 'VERIFIED_NO_SUBSCRIPTION' // Verified + active license, but not subscribed
  | 'VERIFIED_EXPIRING_LICENSE'; // Verified + active license expiring soon

// ============================================================================
// RESOLUTION RESULT
// ============================================================================

export interface ProStatusResolution {
  /**
   * Primary lifecycle state (drives all UI decisions)
   */
  state: ProStatus;
  
  /**
   * Badge display flags (what to show in sidebar/header)
   */
  badges: {
    verified: boolean;          // Show "✓ Verified" badge
    listed: boolean;            // Show "● Listed" badge
    hasSubscription: boolean;   // Show "● Pro Subscription" badge
    showLicenseWarning: boolean; // Show "License Expiring" mini-badge
    daysUntilExpiration: number | null; // Days until license expires (for warning)
  };
  
  /**
   * Blocking banner (highest priority message)
   * Only ONE banner should be shown at a time
   */
  blockingBanner: {
    type: 'critical' | 'warning' | 'info';
    severity: number; // 1-10 (1 = most critical, used for sorting)
    title: string;
    message: string;
    actionLabel?: string;
    actionRoute?: string;
    metadata?: {
      rejectionReason?: string;
      accountStatusReason?: string;
      expiredLicenses?: LicenseDocument[];
      expiringLicenses?: LicenseDocument[];
      reVerificationDeadline?: Date;
    };
  } | null;
  
  /**
   * Lifecycle control flags
   */
  showOnboarding: boolean;      // Show Quick Start panel (embedded in page)
  canAcceptRequests: boolean;   // Can accept review requests from marketplace
  canBeListedInMarketplace: boolean; // Can toggle marketplace listing
  canAccessVerifiedFeatures: boolean; // Can access verification-gated features
  
  /**
   * Navigation gating (which routes should show locks)
   */
  gatedRoutes: {
    reviewRequests: boolean;   // Lock /pro/requests
    conflictChecks: boolean;   // Lock /pro/conflicts
    exports: boolean;          // Lock /pro/dashboard/exports
    marketplaceListing: boolean; // Lock /pro/dashboard/listing
  };
  
  /**
   * Debug metadata (for troubleshooting)
   */
  metadata: {
    adminVerified: boolean;
    hasActiveLicense: boolean;
    hasSubscription: boolean;
    accountModeration: 'active' | 'paused' | 'banned' | null;
    licenseValidation: ReturnType<typeof validateLicenses>;
  };
}

// ============================================================================
// STATUS RESOLUTION FUNCTION
// ============================================================================

/**
 * Resolve Pro status from raw Firestore data
 * This is the ONLY function that should compute Pro status
 * 
 * @param user - Firebase Auth user (or null if not authenticated)
 * @param professional - Professional Firestore document (or null if doesn't exist)
 * @returns ProStatusResolution object (never null - always returns a valid state)
 */
export function resolveProStatus(
  user: User | null,
  professional: Professional | null
): ProStatusResolution {
  
  // ========================================
  // GUARD: No user or no professional profile
  // ========================================
  if (!user || !professional) {
    return createDefaultResolution('ONBOARDING');
  }
  
  // ========================================
  // EXTRACT DATA SOURCES
  // ========================================
  const verification = professional.verification;
  const subscription = professional.subscription;
  const marketplace = professional.marketplace;
  const accountStatus = professional.accountStatus || 'active';
  
  // Admin verification flag
  const adminVerified = verification?.isVerified ?? false;
  
  // License verification adminStatus (Active/Inactive/Suspended)
  // If Suspended, override even if isVerified=true
  const licenseAdminStatus = (verification as { adminStatus?: string })?.adminStatus;
  
  // Validate licenses
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const licenses = (professional.profile as any)?.licenses as LicenseDocument[] | undefined;
  const licenseValidation = validateLicenses(licenses);
  const hasActiveLicense = licenseValidation.hasActiveLicense;
  const hasExpiringLicense = licenseValidation.hasExpiringLicense;
  const daysUntilExpiration = licenseValidation.daysUntilExpiration;
  
  // True verification = admin verified AND has active license
  const isVerified = adminVerified && hasActiveLicense;
  
  // Subscription status
  const hasActiveSubscription = 
    subscription?.status === 'active_pro_subscription' ||
    subscription?.status === 'sponsored_by_user';
  
  // Marketplace listing
  const isListed = marketplace?.isListed ?? false;
  
  // ========================================
  // PRIORITY 1: CRITICAL BLOCKING STATES
  // ========================================
  
  // Admin moderation takes highest priority
  if (accountStatus === 'banned') {
    return createSuspendedAdminResolution('banned', professional);
  }
  
  if (accountStatus === 'paused') {
    return createSuspendedAdminResolution('paused', professional);
  }

  // License verification suspended by admin (bar license issue)
  if (licenseAdminStatus === 'Suspended') {
    return createSuspendedAdminResolution('paused', professional);
  }
  
  // License suspension (admin verified but license expired/missing)
  if (adminVerified && !hasActiveLicense) {
    return createSuspendedLicenseResolution(professional, licenseValidation);
  }
  
  // ========================================
  // PRIORITY 2: ONBOARDING STATES
  // ========================================
  
  // Verification rejected
  if (verification?.status === 'rejected') {
    return createRejectedResolution(professional);
  }
  
  // Re-verification required
  if (verification?.requiresReVerification) {
    return createReVerificationRequiredResolution(professional);
  }
  
  // Pending verification (submitted but not approved)
  if (!adminVerified && verification?.status === 'pending') {
    return createPendingVerificationResolution(professional);
  }
  
  // New pro (never submitted verification)
  if (!adminVerified) {
    return createOnboardingResolution(professional);
  }
  
  // ========================================
  // PRIORITY 3: ACTIVE STATES
  // ========================================
  
  // Verified + Active + Expiring License (warning state)
  if (isVerified && hasExpiringLicense && daysUntilExpiration !== null) {
    return createVerifiedExpiringLicenseResolution(
      professional,
      licenseValidation,
      hasActiveSubscription,
      isListed,
      daysUntilExpiration
    );
  }
  
  // Verified + Active + Subscribed (ideal state)
  if (isVerified && hasActiveSubscription) {
    return createVerifiedActiveResolution(professional, licenseValidation, isListed);
  }
  
  // Verified + Active but NOT subscribed
  if (isVerified && !hasActiveSubscription) {
    return createVerifiedNoSubscriptionResolution(professional, licenseValidation, isListed);
  }
  
  // ========================================
  // FALLBACK: Should never reach here
  // ========================================
  console.warn('[resolveProStatus] Unhandled state combination', {
    adminVerified,
    hasActiveLicense,
    hasActiveSubscription,
    accountStatus,
    verificationStatus: verification?.status
  });
  
  return createDefaultResolution('ONBOARDING');
}

// ============================================================================
// RESOLUTION BUILDERS (Private Helpers)
// ============================================================================

function createDefaultResolution(state: ProStatus): ProStatusResolution {
  return {
    state,
    badges: {
      verified: false,
      listed: false,
      hasSubscription: false,
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: null,
    showOnboarding: state === 'ONBOARDING',
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: false,
      hasActiveLicense: false,
      hasSubscription: false,
      accountModeration: null,
      licenseValidation: validateLicenses(undefined),
    },
  };
}

function createSuspendedAdminResolution(
  reason: 'banned' | 'paused',
  professional: Professional
): ProStatusResolution {
  const isBanned = reason === 'banned';
  
  return {
    state: 'SUSPENDED_ADMIN',
    badges: {
      verified: false, // Don't show verified badge when account is moderated
      listed: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: {
      type: 'critical',
      severity: 1, // Highest priority
      title: isBanned ? 'Account Banned' : 'Account Paused by Admin',
      message: isBanned
        ? 'Your professional account has been banned from the ThreadLock marketplace. All marketplace activities are blocked.'
        : 'Your professional account has been temporarily paused by ThreadLock administrators. You cannot accept new review requests until your account is restored.',
      metadata: {
        accountStatusReason: professional.accountStatusReason || undefined,
      },
    },
    showOnboarding: false,
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: professional.verification?.isVerified ?? false,
      hasActiveLicense: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      accountModeration: reason,
      licenseValidation: validateLicenses(undefined),
    },
  };
}

function createSuspendedLicenseResolution(
  professional: Professional,
  licenseValidation: ReturnType<typeof validateLicenses>
): ProStatusResolution {
  return {
    state: 'SUSPENDED_LICENSE',
    badges: {
      verified: false, // Verification is suspended due to license issue
      listed: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: {
      type: 'critical',
      severity: 2, // Second highest priority (after admin moderation)
      title: 'Verification Suspended - No Active License',
      message: 
        'Your professional verification has been suspended because you do not have an active license on file. ' +
        'You must upload a current, unexpired license to restore your verified status.',
      actionLabel: 'Upload Current License',
      actionRoute: '/pro/setup',
      metadata: {
        expiredLicenses: licenseValidation.expiredLicenses,
      },
    },
    showOnboarding: false, // Don't show onboarding when License is critical issue
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: professional.verification?.isVerified ?? false,
      hasActiveLicense: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

function createRejectedResolution(professional: Professional): ProStatusResolution {
  return {
    state: 'REJECTED',
    badges: {
      verified: false,
      listed: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: {
      type: 'critical',
      severity: 3,
      title: 'Verification Rejected',
      message: 
        'Your verification request was rejected. Please review the reason below and resubmit your documentation.',
      actionLabel: 'Resubmit Verification Documents',
      actionRoute: '/pro/setup',
      metadata: {
        rejectionReason: professional.verification?.rejectionReason || undefined,
      },
    },
    showOnboarding: false,
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: false,
      hasActiveLicense: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      accountModeration: professional.accountStatus || 'active',
      licenseValidation: validateLicenses(undefined),
    },
  };
}

function createReVerificationRequiredResolution(professional: Professional): ProStatusResolution {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const licenses = (professional.profile as any)?.licenses as LicenseDocument[] | undefined;
  const licenseValidation = validateLicenses(licenses);
  
  return {
    state: 'REVERIFICATION_REQUIRED',
    badges: {
      verified: false, // Verification is suspended pending re-verification
      listed: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: {
      type: 'warning',
      severity: 4,
      title: 'Re-Verification Required',
      message: 
        'You must re-submit verification documents to continue marketplace activities.',
      actionLabel: 'Resubmit Verification',
      actionRoute: '/pro/verification',
      metadata: {
        rejectionReason: professional.verification?.reVerificationReason || undefined,
        reVerificationDeadline: professional.verification?.reVerificationDeadline 
          ? new Date(professional.verification.reVerificationDeadline.toDate())
          : undefined,
      },
    },
    showOnboarding: false,
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: professional.verification?.isVerified ?? false,
      hasActiveLicense: licenseValidation.hasActiveLicense,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

function createPendingVerificationResolution(professional: Professional): ProStatusResolution {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const licenses = (professional.profile as any)?.licenses as LicenseDocument[] | undefined;
  const licenseValidation = validateLicenses(licenses);
  
  return {
    state: 'PENDING_VERIFICATION',
    badges: {
      verified: false,
      listed: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: {
      type: 'info',
      severity: 6,
      title: 'Verification Pending',
      message: 
        'Your account is awaiting verification by ThreadLock staff. Please ensure you have submitted your bar license number and jurisdiction(s). ' +
        'You will be notified via email when verification is complete.',
      actionLabel: undefined,
      actionRoute: undefined,
    },
    showOnboarding: false, // Don't show onboarding while waiting for admin
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: false,
      hasActiveLicense: licenseValidation.hasActiveLicense,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

function createOnboardingResolution(professional: Professional): ProStatusResolution {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const licenses = (professional.profile as any)?.licenses as LicenseDocument[] | undefined;
  const licenseValidation = validateLicenses(licenses);
  
  return {
    state: 'ONBOARDING',
    badges: {
      verified: false,
      listed: false,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: null, // No banner - show Quick Start panel instead
    showOnboarding: true, // Show embedded Quick Start panel
    canAcceptRequests: false,
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: false,
    gatedRoutes: {
      reviewRequests: true,
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: false,
      hasActiveLicense: licenseValidation.hasActiveLicense,
      hasSubscription: professional.subscription?.status === 'active_pro_subscription',
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

function createVerifiedExpiringLicenseResolution(
  professional: Professional,
  licenseValidation: ReturnType<typeof validateLicenses>,
  hasActiveSubscription: boolean,
  isListed: boolean,
  daysUntilExpiration: number
): ProStatusResolution {
  return {
    state: 'VERIFIED_EXPIRING_LICENSE',
    badges: {
      verified: true,
      listed: isListed,
      hasSubscription: hasActiveSubscription,
      showLicenseWarning: true, // Show warning mini-badge in sidebar
      daysUntilExpiration,
    },
    blockingBanner: {
      type: 'warning',
      severity: 7,
      title: 'License Expiring Soon',
      message: `Your professional license will expire in ${daysUntilExpiration} day${daysUntilExpiration !== 1 ? 's' : ''}. ` +
        'To maintain your verified status, please upload your renewed license before it expires.',
      actionLabel: 'Upload Renewed License',
      actionRoute: '/pro/setup',
      metadata: {
        expiringLicenses: licenseValidation.expiringLicenses,
      },
    },
    showOnboarding: false,
    canAcceptRequests: true, // Can still accept requests (just a warning)
    canBeListedInMarketplace: hasActiveSubscription,
    canAccessVerifiedFeatures: true,
    gatedRoutes: {
      reviewRequests: false,
      conflictChecks: false,
      exports: false,
      marketplaceListing: !hasActiveSubscription, // Lock if no subscription
    },
    metadata: {
      adminVerified: true,
      hasActiveLicense: true,
      hasSubscription: hasActiveSubscription,
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

function createVerifiedActiveResolution(
  professional: Professional,
  licenseValidation: ReturnType<typeof validateLicenses>,
  isListed: boolean
): ProStatusResolution {
  return {
    state: 'VERIFIED_ACTIVE',
    badges: {
      verified: true,
      listed: isListed,
      hasSubscription: true,
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: null, // No banner - ideal state
    showOnboarding: false,
    canAcceptRequests: true,
    canBeListedInMarketplace: true,
    canAccessVerifiedFeatures: true,
    gatedRoutes: {
      reviewRequests: false, // All routes unlocked
      conflictChecks: false,
      exports: false,
      marketplaceListing: false,
    },
    metadata: {
      adminVerified: true,
      hasActiveLicense: true,
      hasSubscription: true,
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

function createVerifiedNoSubscriptionResolution(
  professional: Professional,
  licenseValidation: ReturnType<typeof validateLicenses>,
  isListed: boolean
): ProStatusResolution {
  return {
    state: 'VERIFIED_NO_SUBSCRIPTION',
    badges: {
      verified: true,
      listed: false, // Can't be listed without subscription
      hasSubscription: false,
      showLicenseWarning: false,
      daysUntilExpiration: null,
    },
    blockingBanner: {
      type: 'info',
      severity: 8,
      title: 'Subscription Required',
      message: 
        'You are verified! To be listed in the marketplace and accept review requests, ' +
        'please subscribe to the ThreadLock Professional plan.',
      actionLabel: 'Subscribe Now',
      actionRoute: '/pro/billing',
    },
    showOnboarding: false,
    canAcceptRequests: false, // Need subscription to accept requests
    canBeListedInMarketplace: false,
    canAccessVerifiedFeatures: true, // Can access other features (not marketplace)
    gatedRoutes: {
      reviewRequests: true, // Lock marketplace features
      conflictChecks: true,
      exports: true,
      marketplaceListing: true,
    },
    metadata: {
      adminVerified: true,
      hasActiveLicense: true,
      hasSubscription: false,
      accountModeration: professional.accountStatus || 'active',
      licenseValidation,
    },
  };
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

/**
 * Check if a status is blocking (prevents marketplace activity)
 */
export function isBlockingStatus(status: ProStatus): boolean {
  return [
    'SUSPENDED_LICENSE',
    'SUSPENDED_ADMIN',
    'REJECTED',
  ].includes(status);
}

/**
 * Check if a status allows accepting review requests
 */
export function canAcceptRequests(resolution: ProStatusResolution): boolean {
  return resolution.canAcceptRequests;
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: ProStatus): string {
  const labels: Record<ProStatus, string> = {
    SUSPENDED_LICENSE: 'Verification Suspended (License)',
    SUSPENDED_ADMIN: 'Account Suspended',
    REJECTED: 'Verification Rejected',
    ONBOARDING: 'Getting Started',
    PENDING_VERIFICATION: 'Verification Pending',
    REVERIFICATION_REQUIRED: 'Re-Verification Required',
    VERIFIED_ACTIVE: 'Verified & Active',
    VERIFIED_NO_SUBSCRIPTION: 'Verified (No Subscription)',
    VERIFIED_EXPIRING_LICENSE: 'Verified (License Expiring)',
  };
  
  return labels[status];
}
